import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Camera,
  Box,
  Scan,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
  Smartphone,
  Layers,
  Move3D,
  Circle,
  Square,
} from 'lucide-react';
import { toast } from 'sonner';
import { captureManager, DeviceCapabilities } from '../services/nativeLidarBridge';

const Capture3D = ({
  roomName = 'Room',
  onCapture,
  onComplete,
  mode = 'auto', // auto, lidar, photogrammetry
}) => {
  // State
  const [capabilities, setCapabilities] = useState(null);
  const [activeMode, setActiveMode] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureStatus, setCaptureStatus] = useState(null);
  const [captures, setCaptures] = useState([]);
  const [stream, setStream] = useState(null);
  const [showGuidance, setShowGuidance] = useState(true);
  
  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Initialize capabilities
  useEffect(() => {
    const init = async () => {
      const result = await captureManager.initialize();
      setCapabilities(result.capabilities);
      setActiveMode(mode === 'auto' ? result.recommendedMode : mode);
    };
    init();
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [mode]);
  
  // Start camera
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      
      // Start capture session
      await captureManager.startCapture(roomName, { forceMode: activeMode });
      setIsCapturing(true);
      setCaptureStatus(captureManager.getStatus());
      
    } catch (err) {
      toast.error('Failed to access camera');
      console.error('Camera error:', err);
    }
  };
  
  // Stop camera
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCapturing(false);
  };
  
  // Capture frame
  const captureFrame = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    
    // Capture with 3D manager
    const result = await captureManager.capture(dataUrl, {
      roomName,
      timestamp: new Date().toISOString(),
    });
    
    // Update local state
    setCaptures(prev => [...prev, {
      id: result.imageId || `cap_${Date.now()}`,
      dataUrl,
      timestamp: new Date().toISOString(),
    }]);
    
    // Update status
    setCaptureStatus(captureManager.getStatus());
    
    // Notify parent
    if (onCapture) {
      onCapture({ dataUrl, result });
    }
    
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    
  }, [roomName, onCapture]);
  
  // Complete capture
  const completeCapture = async () => {
    stopCamera();
    
    const result = await captureManager.finalize();
    
    if (onComplete) {
      onComplete({
        ...result,
        captures,
        roomName,
      });
    }
    
    toast.success('3D capture complete!');
  };
  
  // Get mode icon
  const getModeIcon = () => {
    switch (activeMode) {
      case 'lidar':
        return <Scan className="h-5 w-5" />;
      case 'depth_sensing':
        return <Layers className="h-5 w-5" />;
      default:
        return <Camera className="h-5 w-5" />;
    }
  };
  
  // Get mode label
  const getModeLabel = () => {
    switch (activeMode) {
      case 'lidar':
        return 'LiDAR Scanning';
      case 'depth_sensing':
        return 'Depth Sensing';
      case 'webxr_photogrammetry':
        return 'AR Capture';
      default:
        return 'Photo Capture';
    }
  };
  
  // Calculate coverage
  const getCoverage = () => {
    if (captureStatus?.coverage !== undefined) {
      return captureStatus.coverage;
    }
    if (captureStatus?.estimatedCoverage !== undefined) {
      return captureStatus.estimatedCoverage;
    }
    return Math.min(100, (captures.length / 20) * 100);
  };
  
  // Render guidance overlay
  const renderGuidance = () => {
    if (!showGuidance || !captureStatus) return null;
    
    const guidance = captureStatus.message || 'Capture the room from multiple angles';
    
    return (
      <div className="absolute top-4 left-4 right-4 bg-black/70 rounded-lg p-3">
        <div className="flex items-center gap-2 text-white text-sm">
          <Move3D className="h-4 w-4 text-blue-400" />
          <span>{guidance}</span>
        </div>
        {captureStatus.suggestedAngles?.length > 0 && (
          <div className="flex gap-2 mt-2">
            {captureStatus.suggestedAngles.slice(0, 4).map((angle, i) => (
              <Badge key={i} variant="outline" className="text-xs text-white border-white/30">
                {angle.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  // Render capture grid overlay
  const renderCaptureGrid = () => (
    <div className="absolute inset-0 pointer-events-none">
      {/* Grid lines */}
      <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
        {[...Array(9)].map((_, i) => (
          <div key={i} className="border border-white/20" />
        ))}
      </div>
      
      {/* Center crosshair for 3D alignment */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative">
          <div className="absolute -top-4 left-1/2 w-px h-4 bg-white/50 -translate-x-1/2" />
          <div className="absolute -bottom-4 left-1/2 w-px h-4 bg-white/50 -translate-x-1/2" />
          <div className="absolute top-1/2 -left-4 h-px w-4 bg-white/50 -translate-y-1/2" />
          <div className="absolute top-1/2 -right-4 h-px w-4 bg-white/50 -translate-y-1/2" />
          <Circle className="h-6 w-6 text-white/50" />
        </div>
      </div>
      
      {/* Mode indicator */}
      <div className="absolute top-4 right-4">
        <Badge className="bg-indigo-600">
          {getModeIcon()}
          <span className="ml-1">{getModeLabel()}</span>
        </Badge>
      </div>
    </div>
  );
  
  if (!capabilities) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
          <p className="text-sm text-slate-500">Detecting device capabilities...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="capture-3d-component">
      {/* Capability Status */}
      {!isCapturing && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Box className="h-4 w-4" />
              3D Capture Mode
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Mode selection */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={activeMode === 'lidar' ? 'default' : 'outline'}
                  onClick={() => setActiveMode('lidar')}
                  disabled={!capabilities.hasLiDAR}
                  className={!capabilities.hasLiDAR ? 'opacity-50' : ''}
                >
                  <Scan className="h-4 w-4 mr-1" />
                  LiDAR
                  {capabilities.hasLiDAR && (
                    <CheckCircle2 className="h-3 w-3 ml-1 text-green-500" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant={activeMode === 'photo_photogrammetry' ? 'default' : 'outline'}
                  onClick={() => setActiveMode('photo_photogrammetry')}
                >
                  <Camera className="h-4 w-4 mr-1" />
                  Photo
                </Button>
              </div>
              
              {/* Capability info */}
              {!capabilities.hasLiDAR && (
                <Alert>
                  <Smartphone className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    LiDAR not available on this device. Using photo-based 3D capture 
                    which requires capturing multiple angles for best results.
                  </AlertDescription>
                </Alert>
              )}
              
              {capabilities.hasLiDAR && activeMode === 'lidar' && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-xs text-green-800">
                    LiDAR sensor detected. High-precision 3D scanning available.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Camera View */}
      <div className="relative">
        {isCapturing ? (
          <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            
            {renderCaptureGrid()}
            {renderGuidance()}
            
            {/* Progress bar */}
            <div className="absolute bottom-20 left-4 right-4">
              <div className="bg-black/70 rounded-lg p-2">
                <div className="flex items-center justify-between text-white text-xs mb-1">
                  <span>Coverage</span>
                  <span>{getCoverage().toFixed(0)}%</span>
                </div>
                <Progress value={getCoverage()} className="h-2" />
              </div>
            </div>
            
            {/* Capture controls */}
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
              <Button
                size="lg"
                variant="secondary"
                onClick={stopCamera}
              >
                <Square className="h-5 w-5" />
              </Button>
              <Button
                size="lg"
                className="w-16 h-16 rounded-full bg-white hover:bg-slate-200"
                onClick={captureFrame}
              >
                {activeMode === 'lidar' ? (
                  <Scan className="h-8 w-8 text-indigo-600" />
                ) : (
                  <Camera className="h-8 w-8 text-indigo-600" />
                )}
              </Button>
              {captures.length >= 10 && (
                <Button
                  size="lg"
                  variant="default"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={completeCapture}
                >
                  <CheckCircle2 className="h-5 w-5" />
                </Button>
              )}
            </div>
          </div>
        ) : (
          <button
            onClick={startCamera}
            className="w-full aspect-[4/3] rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex flex-col items-center justify-center hover:border-indigo-400 hover:bg-slate-50 transition-all"
          >
            <Box className="h-12 w-12 text-indigo-400 mb-2" />
            <span className="text-indigo-600 font-medium">Start 3D Capture</span>
            <span className="text-sm text-slate-500 mt-1">
              {activeMode === 'lidar' ? 'LiDAR Scanning' : 'Photo-based 3D'}
            </span>
          </button>
        )}
      </div>
      
      {/* Hidden canvas */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Capture thumbnails */}
      {captures.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-slate-700">
              Captured ({captures.length})
            </h4>
            {captures.length < 10 && (
              <span className="text-xs text-amber-600">
                {10 - captures.length} more for minimum coverage
              </span>
            )}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {captures.slice(-8).map((cap) => (
              <div
                key={cap.id}
                className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border"
              >
                <img
                  src={cap.dataUrl}
                  alt="Capture"
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            {captures.length > 8 && (
              <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center">
                <span className="text-sm text-slate-500">+{captures.length - 8}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Capture3D;
